import { useState, useRef, useEffect } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, Check, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { formations } from "@/data/formations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    formation: "",
    message: "",
    consent: true,
    website: "", // honeypot
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    startedAtRef.current = Date.now();
  }, []);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Merci d'accepter le traitement de vos données.");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Votre message doit contenir au moins 10 caractères.");
      return;
    }
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        started_at: startedAtRef.current,
      });
      setStatus("success");
      toast.success("Demande envoyée. Un conseiller vous recontacte sous 24 h.");
      setForm({ name: "", email: "", phone: "", company: "", formation: "", message: "", consent: true, website: "" });
      startedAtRef.current = Date.now();
    } catch (err) {
      console.error(err);
      setStatus("error");
      const httpStatus = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (httpStatus === 429) {
        msg = typeof detail === "string" ? detail : "Trop de demandes envoyées. Réessayez dans une heure.";
      } else if (httpStatus === 400) {
        msg = "Demande invalide. Merci de remplir le formulaire correctement.";
      } else if (httpStatus === 422) {
        msg = "Certains champs sont mal renseignés. Vérifiez votre email et votre message.";
      } else {
        msg = "Une erreur est survenue. Réessayez ou écrivez-nous à yaformis@gmail.com.";
      }
      setErrMsg(msg);
      toast.error("Échec de l'envoi");
    }
  };

  return (
    <>
      <SEO
        title="Contact — Demandez votre devis formation IA"
        description="Contactez YAFORMIS pour un devis personnalisé. Réponse sous 24h. Conseil pédagogique gratuit. Email yaformis@gmail.com · Téléphone 06 26 34 55 97."
        path="/contact"
      />
      <section className="bg-brand-page py-12 md:py-16 border-b border-brand-line" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">Contact</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-brand-ink max-w-4xl">
            Parlons de votre <span className="text-brand-blue">projet IA.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
            Devis personnalisé sous 24 h ouvrées. Audit pédagogique gratuit pour les groupes de plus de quatre personnes.
          </p>
        </div>
      </section>

      <section className="bg-brand-surface py-12 md:py-16" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <ContactInfo icon={Mail} label="Email" value="yaformis@gmail.com" href="mailto:yaformis@gmail.com" testid="contact-info-email" />
            <ContactInfo icon={Phone} label="Téléphone" value="06 26 34 55 97" href="tel:+33626345597" testid="contact-info-phone" />
            <ContactInfo icon={MapPin} label="Adresse" value="77 Bis rue Pierre Brossolette, 95200 Sarcelles" testid="contact-info-location" />
              <div className="bg-brand-ink text-white p-6 mt-6">
              <div className="overline" style={{ color: "#FF4500" }}>Garantie</div>
              <div className="font-heading font-bold text-lg mt-2">Réponse sous 24 h ouvrées</div>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">Un conseiller pédagogique étudie votre demande personnellement et vous propose un programme adapté à votre métier et à votre niveau.</p>
            </div>
          </div>

          <div className="lg:col-span-8">
            {status === "success" ? (
              <SuccessState onReset={() => setStatus("idle")} />
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border border-brand-line p-6 md:p-10 space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Nom complet *" value={form.name} onChange={onChange("name")} required testid="contact-input-name" />
                  <Field label="Email professionnel *" type="email" value={form.email} onChange={onChange("email")} required testid="contact-input-email" />
                  <Field label="Téléphone" value={form.phone} onChange={onChange("phone")} testid="contact-input-phone" />
                  <Field label="Entreprise" value={form.company} onChange={onChange("company")} testid="contact-input-company" />
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-ink mb-2">Formation visée</label>
                  <select
                    value={form.formation}
                    onChange={onChange("formation")}
                    data-testid="contact-input-formation"
                    className="w-full px-4 py-3 border border-brand-line bg-white text-brand-ink focus:outline-none focus:border-brand-ink transition-colors"
                  >
                    <option value="">— Indéterminé / besoin de conseil —</option>
                    {formations.map((f) => (
                      <option key={f.slug} value={f.title}>{f.title}</option>
                    ))}
                    <option value="Sur mesure">Programme sur mesure</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-ink mb-2">Votre projet *</label>
                  <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={5}
                    data-testid="contact-input-message"
                    placeholder="Quelques lignes sur votre contexte, vos objectifs, le nombre de stagiaires, votre échéance…"
                    className="w-full px-4 py-3 border border-brand-line bg-white text-brand-ink focus:outline-none focus:border-brand-ink resize-none transition-colors"
                  />
                </div>

                {/* Honeypot — hidden from humans, irresistible to bots */}
                <div className="hidden" aria-hidden="true">
                  <label>
                    Site web
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange("website")}
                      name="website"
                    />
                  </label>
                </div>

                <label className="flex items-start gap-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={onChange("consent")}
                    data-testid="contact-input-consent"
                    className="mt-1 accent-brand-blue"
                  />
                  <span>J'accepte que YAFORMIS traite mes données pour me recontacter. Données conservées 36 mois max.</span>
                </label>

                {status === "error" && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{errMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-cta w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <ArrowUpRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-sm font-bold text-brand-ink mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full px-4 py-3 border border-brand-line bg-white text-brand-ink focus:outline-none focus:border-brand-ink transition-colors"
      />
    </div>
  );
}

function ContactInfo({ icon: Icon, label, value, href, testid }) {
  const C = href ? "a" : "div";
  return (
    <C href={href} className="block bg-white border border-brand-line p-5 hover:border-brand-ink transition-colors" data-testid={testid}>
      <Icon className="w-5 h-5 text-brand-blue" />
      <div className="text-xs text-slate-500 mt-3 uppercase tracking-wide font-semibold">{label}</div>
      <div className="font-heading font-bold text-lg text-brand-ink mt-1">{value}</div>
    </C>
  );
}

function SuccessState({ onReset }) {
  return (
    <div className="bg-white border border-brand-line p-10 md:p-14 text-center" data-testid="contact-success">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-blue text-white mb-6">
        <Check className="w-8 h-8" />
      </div>
      <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight text-brand-ink">Demande envoyée</h2>
      <p className="mt-4 text-slate-600 max-w-lg mx-auto leading-relaxed">
        Merci ! Un conseiller pédagogique YAFORMIS vous recontacte sous 24h ouvrées avec un programme adapté à votre projet.
      </p>
      <button onClick={onReset} className="mt-8 btn-ghost" data-testid="contact-success-reset">Envoyer une autre demande</button>
    </div>
  );
}
